import React, { useEffect, useContext } from 'react';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Image from '@atlaskit/image';
import { Box, Stack, xcss } from '@atlaskit/primitives';

import {
	ExperienceTrackerContext,
	AUTOMATION_MANUAL_TRIGGERS,
} from '@confluence/experience-tracker';

import ErrorImage from '../assets/error.svg';
import { useAutomationMenu } from '../common/ConfluenceManualRulesContainer';

const i18n = defineMessages({
	errorDescription: {
		id: 'automation-menu.modal.error.description',
		defaultMessage: 'This is taking awhile to load. Try refreshing the page.',
		description: "Description for the error section shown when rules can't be fetched",
	},
	errorImageAlt: {
		id: 'automation-menu.modal.error.image.alt',
		defaultMessage: 'Error image',
		description: 'Alternative text for the image that renders when there is an error.',
	},
});

const errorStateStyles = xcss({
	textAlign: 'center',
	marginTop: 'space.500',
});

const imageContainerStyles = xcss({
	display: 'block',
	maxWidth: '116px',
	maxHeight: '156px',
	marginBottom: 'space.300',
	marginTop: 'space.050',
});

const descriptionStyles = xcss({
	maxWidth: '360px',
});

export const AutomationModalErrorState: FC = () => {
	const { formatMessage } = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { analyticsSource } = useAutomationMenu();

	useEffect(() => {
		experienceTracker.fail({
			name: AUTOMATION_MANUAL_TRIGGERS,
			error: new Error('Failed to fetch manual rules'),
			attributes: {
				source: analyticsSource,
			},
		});
	}, [experienceTracker, analyticsSource]);

	return (
		<Stack xcss={errorStateStyles} alignInline="center" alignBlock="center">
			<Box xcss={imageContainerStyles}>
				<Image alt={formatMessage(i18n.errorImageAlt)} src={ErrorImage} />
			</Box>
			<Box xcss={descriptionStyles}>{formatMessage(i18n.errorDescription)}</Box>
		</Stack>
	);
};
